/**
 * LLM Configuration Service
 * Manages user's LLM API keys and execution mode preferences
 */

const STORAGE_KEY = 'myagent_llm_config'

const DEFAULT_CONFIG = {
  executionMode: 'remote', // 'local' | 'remote'
  providers: {},
  defaultProvider: null,
  toolAgentModel: null, // override model for tool agent (e.g. 'gpt-4o-mini')
}

/**
 * Resolve provider and API key from config (sync helper)
 * @param {Object} config - LLM config object
 * @returns {{ provider: string|null, apiKey: string|null }}
 */
export function resolveProviderAndKey(config) {
  if (!config?.providers) return { provider: null, apiKey: null }

  const provider = config.defaultProvider ||
    Object.keys(config.providers).find(p => config.providers[p]?.enabled && config.providers[p]?.apiKey)

  if (!provider) return { provider: null, apiKey: null }

  return {
    provider,
    apiKey: config.providers[provider]?.apiKey || null
  }
}

/**
 * Create LLM config service
 * @param {Object} storageAdapter - Storage adapter with get/set methods
 * @returns {Object} Config service methods
 */
export function createLlmConfigService(storageAdapter) {
  const getConfig = async () => {
    const stored = await storageAdapter.get(STORAGE_KEY)
    if (!stored) return DEFAULT_CONFIG
    try {
      return JSON.parse(stored)
    } catch {
      return DEFAULT_CONFIG
    }
  }

  const saveConfig = async (config) => {
    await storageAdapter.set(STORAGE_KEY, JSON.stringify(config))
  }

  const isLocalMode = async () => {
    const config = await getConfig()
    return config.executionMode === 'local'
  }

  const getProviderConfig = async (provider) => {
    const config = await getConfig()
    return config.providers[provider] || null
  }

  const setProviderConfig = async (provider, providerConfig) => {
    const config = await getConfig()
    config.providers[provider] = providerConfig
    await saveConfig(config)
  }

  const setExecutionMode = async (mode) => {
    const config = await getConfig()
    config.executionMode = mode
    await saveConfig(config)
  }

  const getEnabledProviders = async () => {
    const config = await getConfig()
    return Object.entries(config.providers)
      .filter(([, cfg]) => cfg?.enabled && cfg?.apiKey)
      .map(([name]) => name)
  }

  const getDefaultProvider = async () => {
    const config = await getConfig()
    return resolveProviderAndKey(config).provider
  }

  return {
    getConfig,
    saveConfig,
    isLocalMode,
    getProviderConfig,
    setProviderConfig,
    setExecutionMode,
    getEnabledProviders,
    getDefaultProvider
  }
}
